// DNB - Complete Service Data
// All 26 services with unique, custom-written content

export const serviceStructure = {
  plumbing: {
    name: 'Plumbing',
    slug: 'plumbing',
    description: 'Complete residential and light commercial plumbing services in Calgary',
    icon: 'droplets',
    subServices: [
      {
        name: 'Plumbing Repairs',
        slug: 'plumbing-repairs-calgary',
        metaTitle: 'Plumbing Repairs Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Professional plumbing repairs in Calgary. Leak fixes, faucet repairs, toilet repairs, valve replacements. Call 403-615-8770.',
        heroImage: 'plumbing-repair',
        serviceType: 'repair',
        intro: `General plumbing repairs cover the everyday issues that come up in any home—dripping faucets, running toilets, leaking supply lines, stuck shutoff valves, and fixtures that just aren't working the way they should. These aren't emergencies, but they're annoying, they waste water, and they tend to get worse if left alone.

DNB handles these repairs throughout Calgary. We show up, figure out what's actually going on, fix it properly, and make sure it's working before we leave. No drama, no upselling—just straightforward plumbing work done right.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Faucet that keeps dripping no matter how hard you turn the handle',
          'Toilet that runs constantly or doesn\'t flush properly',
          'Water appearing under the sink, around fixtures, or near supply lines',
          'Shutoff valves that are stuck, corroded, or won\'t seal anymore',
          'Noticeably lower water pressure at one or more fixtures',
          'Small leaks at visible connections or exposed pipes',
          'Unusual sounds from pipes when water is running'
        ],
        whyMatters: `A dripping faucet might not seem like much, but it adds up—wasted water, higher bills, and the slow damage that comes from moisture sitting where it shouldn't. A leak under the sink can ruin the cabinet floor. Water behind the wall leads to mold and drywall repair. A stuck shutoff valve becomes a real problem when you actually need to shut something off.

Most plumbing repairs are straightforward when caught early. The longer they're ignored, the more they cost to fix—and the more likely they are to turn into a bigger issue. Getting them handled now saves hassle and money down the road.`,
        services: [
          'Diagnose the actual source of the plumbing issue',
          'Repair or replace worn-out parts and failed components',
          'Fix leaks at fixtures, valves, supplies, and accessible piping',
          'Restore proper water flow and pressure',
          'Replace shutoff valves that no longer work',
          'Test everything before leaving'
        ],
        whyChoose: `DNB is a local Calgary plumbing company built on straightforward service. We don't overcomplicate simple repairs or turn a leaky faucet into a sales pitch. We show up on time, explain what we find, and fix the problem properly.

Our work is clean, organized, and done with respect for your home. We back supplied repair materials with a 1-year parts and labour warranty—because we stand behind what we install.`,
        cta: 'Book Plumbing Repairs',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Bathroom Plumbing',
        slug: 'bathroom-plumbing-calgary',
        metaTitle: 'Bathroom Plumbing Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Bathroom plumbing services in Calgary. Toilets, sinks, showers, tubs, drains, fixture upgrades. Call 403-615-8770.',
        heroImage: 'bathroom',
        serviceType: 'repair',
        intro: `Bathrooms see more plumbing use than almost any other room in the house. Toilets, sinks, faucets, showers, tubs, drains, shutoffs, and the valves behind your shower walls—there's a lot going on in a small space, and problems here tend to make themselves known quickly.

DNB handles bathroom plumbing throughout Calgary, from simple repairs to full fixture replacements and renovation plumbing. Whether it's a toilet that won't stop running, a shower that lost pressure, or a complete bathroom upgrade, we get it done properly.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Toilet that rocks, leaks at the base, or won\'t flush properly',
          'Bathroom faucet that drips or won\'t shut off completely',
          'Weak or inconsistent shower pressure',
          'Slow-draining sink, tub, or shower',
          'Moisture showing up around the vanity, toilet base, or tub surround',
          'Fixtures that are dated, corroded, or due for replacement',
          'Bathroom renovation that needs plumbing changes'
        ],
        whyMatters: `Bathroom plumbing problems don't stay hidden for long. A toilet leaking at the base can rot the subfloor. A slow shower drain leads to standing water and buildup. Moisture around the vanity damages the cabinet and can spread into the wall behind it. Water from an upstairs bathroom eventually shows up on the ceiling below.

These are high-use fixtures in finished spaces, so the stakes are higher. Problems here affect your daily routine and, left unchecked, they affect your home's structure and finishes. Getting bathroom plumbing right matters.`,
        services: [
          'Toilet repair and replacement',
          'Sink and faucet installation and repairs',
          'Tub and shower plumbing work',
          'Shower valve replacement and repairs',
          'Drain and trap changes',
          'Bathroom rough-ins and fixture hookups during renovations'
        ],
        whyChoose: `We work carefully in finished bathroom spaces—protecting flooring, keeping the area clean, and making sure fixtures are installed properly. Bathroom plumbing affects your daily routine, so we focus on getting it right without dragging out the job.

DNB is a local Calgary company that treats your home with respect. We back supplied materials with a 1-year parts and labour warranty, and we don't leave until the work is done properly.`,
        cta: 'Book Bathroom Plumbing Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Kitchen Plumbing',
        slug: 'kitchen-plumbing-calgary',
        metaTitle: 'Kitchen Plumbing Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Kitchen plumbing services in Calgary. Sinks, faucets, dishwashers, garburators, appliance hookups. Call 403-615-8770.',
        heroImage: 'kitchen',
        serviceType: 'repair',
        intro: `Kitchen plumbing handles a lot—sinks, faucets, drains, dishwasher connections, garburators, water lines for fridges and appliances, and whatever else gets added during a kitchen upgrade. It's one of the most used plumbing areas in any home, and when something goes wrong, it disrupts everything from cooking to cleanup.

DNB provides kitchen plumbing services throughout Calgary. Whether you need a faucet replaced, a dishwasher hooked up, a garburator installed, or plumbing updated as part of a renovation, we handle it cleanly and professionally.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Kitchen faucet leaking at the base or handle',
          'Slow-draining kitchen sink',
          'Dishwasher that needs hookup or replacement',
          'Garburator that jams, leaks, or stopped working',
          'Water line needed for a fridge or other appliance',
          'Leak under the sink causing cabinet damage',
          'Kitchen renovation requiring plumbing updates'
        ],
        whyMatters: `The kitchen is where you cook, clean, and spend time with family—so plumbing problems here are hard to ignore. A leaky faucet drips into the sink or onto the counter. A slow drain backs up during dishes. A leak under the sink can ruin the cabinet floor and spread to the subfloor before you even notice.

Kitchen plumbing affects your daily routine and your home. Getting issues fixed properly—and getting appliances connected right the first time—saves frustration and prevents damage.`,
        services: [
          'Faucet replacement and repairs',
          'Sink plumbing repairs and replacements',
          'Dishwasher hookups and connections',
          'Garburator installation and replacement',
          'Appliance water line connections',
          'Kitchen plumbing updates during renovations'
        ],
        whyChoose: `We understand that kitchen plumbing work needs to be done right and cleaned up properly. We don't leave a mess under your sink or half-finished connections behind your appliances.

DNB is a Calgary-based company focused on practical plumbing work done with care. Supplied repair materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Kitchen Plumbing Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Basement Rough-Ins',
        slug: 'basement-roughins-calgary',
        metaTitle: 'Basement Plumbing Rough-Ins Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Basement plumbing rough-ins in Calgary. Bathrooms, wet bars, laundry, secondary suites. Code-compliant work. Call 403-615-8770.',
        heroImage: 'basement',
        serviceType: 'install',
        intro: `Basement rough-in plumbing is the foundation for everything that comes after—drains, vents, and water lines installed before walls go up and finishing begins. This is the stage that determines where fixtures will go, how drainage will flow, and whether the plumbing passes inspection.

DNB handles basement rough-in work throughout Calgary, whether you're adding a bathroom, planning a wet bar, setting up laundry, or developing the basement into a finished living space. Getting this stage right makes the rest of the project easier.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re developing an unfinished basement',
          'You\'re adding a bathroom, wet bar, or laundry area below grade',
          'You want the plumbing prepared before framing and drywall',
          'You need the layout planned correctly before concrete work is complete',
          'You\'re working toward permit and inspection stages',
          'You want to avoid expensive rework later'
        ],
        whyMatters: `What happens at rough-in stage affects everything that follows. If the drain slope is wrong, it doesn't drain. If the vent isn't where it needs to be, you'll hear gurgling and slow drainage. If fixtures are placed without thinking through the layout, you end up with awkward rooms or code issues.

Mistakes at this stage are expensive to fix—sometimes requiring jackhammering concrete, rerouting through finished walls, or starting over. Getting the rough-in right the first time sets up the rest of the project for success.`,
        services: [
          'Review layout and fixture locations with the homeowner',
          'Rough in drains, vents, and water lines to plan',
          'Prepare plumbing for bathrooms, wet bars, and laundry',
          'Install code-conscious rough-in work ready for inspection',
          'Coordinate with framing, concrete, and other trades',
          'Set up the plumbing so finishing work can proceed cleanly'
        ],
        whyChoose: `Basement development is where planning matters most—and DNB approaches rough-in work with the layout and long-term function in mind. We understand how basement builds come together and where plumbing needs to be before walls close in.

We're a Calgary company with hands-on experience in basement rough-ins. Our work is done right the first time, ready for inspection, and built to last.`,
        cta: 'Request a Basement Rough-In Quote',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Water Heater Replacement',
        slug: 'water-heater-replacement-calgary',
        metaTitle: 'Water Heater Replacement Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Hot water tank replacement in Calgary. Same-day service available. Professional installation. Call 403-615-8770.',
        heroImage: 'water-heater',
        serviceType: 'repair',
        intro: `When a water heater starts failing—or fails completely—it becomes a priority. No hot water means no showers, no dishes, no laundry. And if the tank is leaking, there's water damage to deal with too.

DNB provides water heater replacement throughout Calgary. We remove the old unit, install a properly sized replacement, make sure all connections are safe and up to code, and test everything before we leave. If your tank is showing its age or has already failed, we can help.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Hot water that\'s inconsistent or not as hot as it used to be',
          'Hot water running out faster than before',
          'Rusty or discoloured water from the hot side',
          'Water pooling around the base of the tank',
          'Rumbling, popping, or banging sounds coming from the tank',
          'Tank that\'s 10 to 15 years old and showing signs of wear',
          'Visible rust or corrosion on the tank or fittings'
        ],
        whyMatters: `An aging water heater doesn't always give much warning before it fails. Sometimes there's a slow decline in performance—shorter showers, lukewarm water. Other times, it's a sudden leak or complete failure.

A tank that lets go can dump 40 to 60 gallons of water into your basement. Even a slow leak causes damage over time. And being without hot water in a Calgary winter isn't something anyone wants to deal with for long. Replacing an aging tank before it fails gives you control over the timing—and avoids the emergency.`,
        services: [
          'Remove and dispose of the old water heater',
          'Supply and install a properly sized replacement tank',
          'Replace valves and connections as needed',
          'Test temperature, pressure relief, and operation',
          'Make sure gas or electrical connections are safe',
          'Leave the area clean when finished'
        ],
        whyChoose: `We handle water heater replacements efficiently, without dragging the job out or leaving a mess behind. We help you choose the right size, install it properly, and make sure you've got reliable hot water again.

DNB is a Calgary company focused on getting the job done right. We back supplied materials with a 1-year parts and labour warranty.`,
        cta: 'Book Water Heater Replacement',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Tankless Water Heaters',
        slug: 'tankless-water-heater-calgary',
        metaTitle: 'Tankless Water Heater Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Tankless water heater installation in Calgary. On-demand hot water, space savings. Professional setup. Call 403-615-8770.',
        heroImage: 'tankless',
        serviceType: 'install',
        intro: `Tankless water heaters heat water on demand instead of storing it in a large tank. They're compact, wall-mounted, and can provide continuous hot water for households that use a lot of it. For some homes, they're a smart upgrade—for others, a traditional tank still makes more sense.

DNB installs tankless water heaters in Calgary and can help you figure out whether it's the right fit for your home. We don't push tankless systems on everyone—we recommend what actually makes sense for your situation.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You want to free up floor space in the utility area',
          'Your household uses a lot of hot water and runs out frequently',
          'You\'re replacing an aging tank and want to explore more efficient options',
          'You\'re renovating and want to upgrade the mechanical setup',
          'You want continuous hot water availability for back-to-back showers',
          'You\'re building new and want a modern hot water solution'
        ],
        whyMatters: `A tankless system can be a great upgrade—but it's not automatically better for every home. The unit needs to be properly sized for your household's demand. The gas supply needs to be adequate. Venting requirements are different from a standard tank. If any of this is done wrong, you end up with a system that doesn't perform the way you expected.

Proper installation matters more with tankless systems than with standard tanks. Getting it right means reliable, on-demand hot water. Getting it wrong means lukewarm showers and a system that never quite delivers.`,
        services: [
          'Assess your household\'s hot water demand',
          'Recommend proper unit sizing based on fixtures and usage',
          'Install gas, water, and venting connections',
          'Remove old tank system if required',
          'Commission and test the new unit',
          'Walk you through how the system works'
        ],
        whyChoose: `We don't oversell tankless systems—if a traditional tank makes more sense for your home, we'll tell you. But when tankless is the right fit, we size it properly, install it correctly, and make sure it performs the way it should.

DNB is a Calgary company focused on honest recommendations and quality installation. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Request a Tankless Water Heater Quote',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Water Softeners',
        slug: 'water-softeners-calgary',
        metaTitle: 'Water Softeners Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Water softener installation in Calgary. Eliminate hard water buildup, protect fixtures and appliances. Call 403-615-8770.',
        heroImage: 'water-quality',
        serviceType: 'repair',
        intro: `Calgary's water is hard—full of calcium and magnesium that leave white scale on fixtures, spots on dishes, and buildup inside pipes and appliances. A water softener reduces those minerals before they cause problems, protecting your plumbing and improving how the water feels and performs throughout your home.

DNB installs and replaces water softeners for Calgary homeowners. If your home doesn't have one, or if the existing unit isn't working properly, we can help.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'White scale buildup on faucets, showerheads, and glass',
          'Spots and film on dishes and glassware after washing',
          'Soap that doesn\'t lather well',
          'Dry skin or hair after bathing',
          'Scale buildup around fixtures and drains',
          'Appliances like dishwashers and washing machines not performing as well'
        ],
        whyMatters: `Hard water affects almost everything it touches. Scale builds up inside your water heater, reducing efficiency and shortening its life. It accumulates in pipes, gradually restricting flow. It leaves deposits on fixtures that are hard to clean and makes soap less effective.

A water softener addresses the problem at the source. It protects your plumbing and appliances, extends their lifespan, and makes the water throughout your home feel and work better. For Calgary homes, it's often one of the most practical upgrades you can make.`,
        services: [
          'Assess your home\'s water softening needs',
          'Recommend a properly sized system',
          'Install new water softeners',
          'Replace outdated or failing units',
          'Check operation and settings',
          'Set up bypass and drain connections properly'
        ],
        whyChoose: `We install water softeners that are sized for your household and Calgary's water conditions—not oversized units that waste salt or undersized ones that don't keep up. Our installations are clean, properly connected, and set up to work correctly.

DNB is a local Calgary company. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Water Softener Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Water Filtration',
        slug: 'water-filtration-calgary',
        metaTitle: 'Water Filtration Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Water filtration systems in Calgary. Under-sink filters, whole-home filtration, cleaner drinking water. Call 403-615-8770.',
        heroImage: 'water-quality',
        serviceType: 'install',
        intro: `Water filtration improves the water you drink, cook with, and use throughout your home. Whether you want cleaner drinking water at the kitchen tap, reduced chlorine taste, or whole-home filtration for sediment and general water quality, there are options that fit different needs and budgets.

DNB installs water filtration systems for Calgary homeowners. We help you figure out what you're trying to improve, recommend an appropriate setup, and install it properly.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You don\'t like the taste or smell of your tap water',
          'You notice sediment or discolouration',
          'You want cleaner drinking water without buying bottles',
          'You\'re concerned about chlorine or general water quality',
          'You want an under-sink filter for drinking water',
          'You want whole-home filtration for better water everywhere'
        ],
        whyMatters: `The water in your home may be safe to drink, but that doesn't mean it tastes great or feels clean. Chlorine, sediment, and dissolved minerals affect how the water tastes, smells, and interacts with appliances. Some homeowners want better drinking water. Others want cleaner water for the whole house.

The right filtration setup depends on what you're trying to achieve. A simple under-sink filter handles drinking water. Whole-home systems address everything coming into the house. We help you choose what actually makes sense—not the most expensive option, but the right one for your needs.`,
        services: [
          'Review what you want to improve about your water',
          'Recommend an appropriate filtration setup',
          'Install under-sink or whole-home filtration systems',
          'Replace outdated filtration equipment',
          'Confirm proper operation and clean connections',
          'Provide guidance on filter replacement intervals'
        ],
        whyChoose: `We recommend filtration systems that actually fit your needs—not one-size-fits-all solutions that don't solve your problem or overpriced setups you don't need. Our installs are clean, functional, and set up to work properly.

DNB is a Calgary company focused on practical solutions. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Water Filtration Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Drain Cleaning',
        slug: 'drain-cleaning-calgary',
        metaTitle: 'Drain Cleaning Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Drain cleaning services in Calgary. Clogged sinks, tubs, showers, floor drains. Fast response. Call 403-615-8770.',
        heroImage: 'drain',
        serviceType: 'repair',
        intro: `Clogged drains are one of the most common plumbing problems Calgary homeowners deal with—slow kitchen sinks, standing water in the shower, bathroom drains that won't clear. Sometimes it's buildup that's accumulated over time. Other times, something got into the line that doesn't belong there.

DNB provides drain cleaning services throughout Calgary. We clear the blockage, restore proper flow, and let you know if there's something bigger going on that needs attention.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Sink, tub, or shower draining slowly',
          'Standing water that takes forever to clear',
          'Repeated clogs in the same fixture',
          'Gurgling sounds when water drains',
          'Unpleasant odours coming from a drain',
          'Water backing up when it shouldn\'t'
        ],
        whyMatters: `A slow drain is easy to ignore at first, but it usually gets worse. What starts as a minor annoyance turns into standing water, backups, and eventually a drain that doesn't work at all. Repeated chemical treatments can damage pipes without solving the underlying problem.

Getting the drain cleared properly—and knowing whether it's a simple clog or part of a bigger issue—saves time and frustration. We assess what's going on, clear it correctly, and give you honest feedback on what we find.`,
        services: [
          'Assess the likely cause and location of the blockage',
          'Clear the drain professionally',
          'Restore proper drainage flow',
          'Check for signs of recurring problems',
          'Advise if symptoms suggest a larger line issue'
        ],
        whyChoose: `We don't treat every slow drain like a major emergency or turn a simple clog into a sales pitch. We clear the drain, restore flow, and give you honest information about what's going on.

DNB is a local Calgary plumbing company. We show up on time, do the work properly, and leave your home the way we found it.`,
        cta: 'Book Drain Cleaning',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Sewer Services',
        slug: 'sewer-repair-calgary',
        metaTitle: 'Sewer Services Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Sewer line services in Calgary. Main line backups, sewer troubleshooting, drainage issues. Call 403-615-8770.',
        heroImage: 'sewer',
        serviceType: 'repair',
        intro: `When multiple drains back up at once—or when the toilet and shower start affecting each other—the problem is usually in the main sewer line, not an individual fixture. This is bigger than a clogged sink, and it needs to be dealt with properly.

DNB helps Calgary homeowners troubleshoot and address main line issues. We assess the situation, determine what's likely going on, and help you understand your options—without overcomplicating it or making things sound worse than they are.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Multiple fixtures backing up at the same time',
          'Toilet and shower affecting each other',
          'Sewer smell in the basement or lower level',
          'Floor drain backups',
          'Repeated main line issues that keep coming back',
          'Slow drainage throughout the house, not just one fixture'
        ],
        whyMatters: `Main sewer issues affect the entire home. When the main line can't drain properly, every fixture becomes a potential backup point. The mess is significant, the sanitation concerns are real, and ignoring it only makes things worse.

Understanding whether the issue is a temporary blockage, a recurring problem, or a sign of something more serious helps you make the right call. We assess what's happening and explain what we find in plain terms—so you can decide how to proceed.`,
        services: [
          'Review symptoms and assess the situation',
          'Determine whether the issue appears related to the main line',
          'Perform sewer-related plumbing repair work within scope',
          'Help you understand likely causes and next steps',
          'Coordinate with excavation or camera services if needed',
          'Provide honest guidance without scare tactics'
        ],
        whyChoose: `We don't dramatize sewer issues or make things sound worse than they are. We assess what's going on, explain it clearly, and help you figure out the right next step—whether that's a repair we can handle or something that needs specialized equipment.

DNB is a Calgary plumbing company focused on honest service and practical solutions.`,
        cta: 'Book Sewer Service Assessment',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Poly-B Replacement',
        slug: 'poly-b-replacement-calgary',
        metaTitle: 'Poly-B Replacement Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Poly-B pipe replacement in Calgary. Replace polybutylene plumbing with modern PEX. Insurance compliance. Call 403-615-8770.',
        heroImage: 'polyb',
        serviceType: 'install',
        intro: `Poly-B—short for polybutylene—is a grey plastic water supply piping that was commonly installed in Calgary homes from the late 1970s through the mid-1990s. At the time, it seemed like a good alternative to copper. Today, it's known for aging poorly and failing without much warning.

DNB specializes in Poly-B replacement throughout Calgary. If your home still has polybutylene plumbing, we can help you replace it with modern, reliable materials—before a failure causes damage.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'Your home has visible grey plastic water lines',
          'Your home was built between the late 1970s and mid-1990s',
          'You\'ve already experienced leaks from Poly-B piping',
          'You\'re renovating and want to replace aging plumbing while walls are open',
          'Your insurance company is asking about Poly-B or adjusting coverage',
          'You want to replace the system before a failure causes damage',
          'You\'re preparing to sell and want to address the issue proactively'
        ],
        whyMatters: `Poly-B doesn't age gracefully. The plastic becomes brittle over time, especially where it's been exposed to chlorine in municipal water, UV light, or heat. Failures often happen at fittings—sometimes slowly, sometimes all at once. A small leak behind a wall can cause mold and drywall damage before you even notice it. A fitting that lets go completely can flood a room in minutes.

Many insurance companies know the risks. Some restrict coverage, increase premiums, or require replacement. If you're selling your home, Poly-B often comes up during the inspection and can affect the deal.

Replacing Poly-B isn't just about avoiding a leak—it's about protecting your home, satisfying insurance requirements, and having peace of mind that your plumbing is reliable.`,
        services: [
          'Inspect the visible Poly-B system and discuss your home\'s layout',
          'Plan full or partial replacement depending on the situation',
          'Replace grey Poly-B lines with modern PEX or PERT piping',
          'Complete clean, organized work with as little disruption as practical',
          'Pressure test the new system',
          'Provide documentation for insurance if required'
        ],
        whyModernMaterials: `Modern replacement materials like PEX and PERT are flexible, durable, and designed to handle the conditions that Poly-B couldn't. They're resistant to chlorine, they expand slightly to resist freezing, and they've been proven in real-world installations for decades. Replacing Poly-B with modern piping gives you a system that will last.`,
        whyChoose: `Poly-B replacement requires careful planning—especially in finished homes where you want to minimize drywall openings and disruption. We approach each project thoughtfully, mapping out the best route and completing the work as cleanly as possible.

DNB is a local Calgary company with extensive Poly-B replacement experience. We back supplied materials with a 1-year parts and labour warranty and stand behind our work completely.`,
        cta: 'Request a Poly-B Replacement Quote',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Backflow Testing',
        slug: 'backflow-testing-calgary',
        metaTitle: 'Backflow Testing Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Certified backflow testing in Calgary. Annual testing, compliance documentation, repairs. Call 403-615-8770.',
        heroImage: 'backflow',
        serviceType: 'compliance',
        intro: `Backflow preventers protect the public water supply from contamination by stopping water from flowing backward through the system. If your property has a backflow device—common on irrigation systems, commercial properties, and certain residential setups—it needs to be tested annually to confirm it's working properly.

DNB provides backflow testing services throughout Calgary. We test the device, verify it's functioning correctly, and provide the documentation you need for compliance.`,
        section2Title: 'When This Service Is Required',
        section2Items: [
          'Your annual backflow test date is due',
          'The City of Calgary or your utility requires compliance documentation',
          'Your property has an existing backflow preventer installed',
          'You manage a commercial, multi-unit, or mixed-use property',
          'You have an irrigation system with a backflow device',
          'A previous test indicated repair issues that have since been addressed'
        ],
        whyMatters: `Backflow prevention isn't optional—it's a safety measure that protects drinking water from contamination. If a backflow device fails, it can allow contaminated water to flow backward into the clean water supply. Annual testing confirms the device is operating as intended.

Most jurisdictions, including Calgary, require annual testing and documentation. Failing to comply can result in notices, fees, or service interruptions. Getting tested on schedule keeps you compliant and confirms your device is doing its job.`,
        services: [
          'Test the existing backflow assembly',
          'Verify device performance meets requirements',
          'Identify failed components or repair needs if present',
          'Provide proper documentation and reporting',
          'Submit results to the city if required',
          'Recommend repairs or replacement if the device doesn\'t pass'
        ],
        whyChoose: `We provide straightforward backflow testing—show up on schedule, test the device properly, and provide the documentation you need. No unnecessary upselling, no complicated reports. Just reliable testing and clear communication.

DNB serves Calgary homeowners and property managers. We understand the compliance requirements and make the process simple.`,
        cta: 'Book Backflow Testing',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Secondary Suite Plumbing',
        slug: 'secondary-suite-plumbing-calgary',
        metaTitle: 'Secondary Suite Plumbing Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Secondary suite plumbing in Calgary. Legal basement suite plumbing, permits, inspections. Call 403-615-8770.',
        heroImage: 'basement',
        serviceType: 'install',
        intro: `Building a secondary suite means adding plumbing for a separate living space—kitchens, bathrooms, laundry, and everything that makes a basement or garage suite functional. The plumbing needs to be planned correctly, installed to code, and ready for inspection stages.

DNB handles secondary suite plumbing throughout Calgary. Whether you're creating a legal basement suite, an in-law suite, or a rental unit, we help you get the plumbing right from the start.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re building or planning a basement suite',
          'You need a kitchen, bathroom, or laundry added for a secondary unit',
          'You need plumbing layout planning before walls go up',
          'You\'re working toward permit approval and inspection stages',
          'You want suite plumbing done properly from the start',
          'You\'re converting existing space into a separate living area'
        ],
        whyMatters: `Secondary suite plumbing has to be done right. Fixture placement, drainage, venting, and water supply all need to work together—and they need to meet code requirements for inspection. Mistakes at this stage delay the project, add costs, and can result in failed inspections.

Getting the plumbing planned and installed correctly means your suite build stays on track. It also means the finished suite functions properly for years to come.`,
        services: [
          'Review the suite layout and fixture placement',
          'Rough in plumbing for bathrooms, kitchens, and laundry',
          'Install fixtures and make final connections when in scope',
          'Coordinate plumbing work with permit and inspection requirements',
          'Work with your overall build timeline',
          'Ensure the plumbing is ready for inspection stages'
        ],
        whyChoose: `We understand Calgary's secondary suite requirements and how basement builds come together. We work with your project timeline, coordinate with other trades, and make sure the plumbing is done right—not just installed, but ready for inspection.

DNB is a local company with real experience in basement development and suite builds.`,
        cta: 'Request Secondary Suite Plumbing Quote',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      }
    ]
  },
  heating: {
    name: 'Heating',
    slug: 'heating',
    description: 'Expert heating services to keep Calgary homes warm',
    icon: 'flame',
    subServices: [
      {
        name: 'Furnace Installation & Repair',
        slug: 'furnace-installation-repair-calgary',
        metaTitle: 'Furnace Installation & Repair Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Furnace repair and installation in Calgary. Diagnose issues, restore heat, replace aging systems. Call 403-615-8770.',
        heroImage: 'furnace',
        serviceType: 'repair',
        intro: `Your furnace is essential for surviving a Calgary winter. When it's running well, you don't think about it. When it's not—whether it's making strange noises, struggling to keep up, or not producing heat at all—it becomes an urgent priority.

DNB provides furnace repair and installation services throughout Calgary. We diagnose problems, make repairs when it makes sense, and help you decide when replacement is the better option. Honest recommendations, quality work, and dependable heat.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Furnace not producing heat',
          'Uneven temperatures throughout the home',
          'System cycling on and off frequently (short cycling)',
          'Strange sounds—banging, squealing, rattling',
          'Noticeably higher heating bills without a clear reason',
          'Furnace that\'s 15 to 20 years old and declining in reliability',
          'Comfort problems during cold weather'
        ],
        whyMatters: `In Calgary, reliable heat isn't optional—it's essential. A furnace that's struggling doesn't just affect your comfort; it works harder, uses more energy, and is more likely to fail completely when temperatures drop. Short cycling, uneven heating, and strange noises are all signs something isn't right.

Getting issues diagnosed and addressed properly—whether that means a repair or a replacement—keeps your home comfortable and avoids the worst-case scenario: a complete failure on the coldest day of the year.`,
        services: [
          'Diagnose furnace faults and determine the cause',
          'Repair failed components when repair is practical',
          'Advise honestly when replacement makes more sense',
          'Size and install new furnace equipment properly',
          'Test airflow, combustion, and system operation',
          'Make sure all connections are safe and up to code'
        ],
        whyChoose: `We don't push replacement when repair makes sense, and we don't waste your time on repairs when the system is past its useful life. We give you honest information so you can make the right call.

DNB is a Calgary heating company focused on dependable service. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Furnace Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Boiler & Hydronic Systems',
        slug: 'boiler-hydronic-calgary',
        metaTitle: 'Boiler & Hydronic Heating Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Boiler repair and hydronic heating services in Calgary. Hot water heating, radiators, in-floor systems. Call 403-615-8770.',
        heroImage: 'boiler',
        serviceType: 'repair',
        intro: `Boilers and hydronic systems heat your home using hot water instead of forced air. The water circulates through pipes to radiators, baseboards, fan coils, or in-floor tubing. It's a different approach than standard furnaces, and it requires a different kind of expertise.

DNB services and installs boiler and hydronic heating systems throughout Calgary. Whether your boiler needs repair, your zones aren't heating evenly, or you're considering a new system, we can help.`,
        section2Title: 'Signs You May Need This Service',
        section2Items: [
          'Uneven heat from zone to zone or room to room',
          'Boiler pressure fluctuating or dropping',
          'Air trapped in radiators or baseboards (cold spots)',
          'Leaks around the boiler or piping',
          'Strange noises from the boiler or circulation system',
          'System not keeping up with heating demand',
          'Older boiler that needs attention or replacement'
        ],
        whyMatters: `Hydronic systems are efficient and comfortable when they're running right—but they need to be properly balanced, pressurized, and maintained. Air in the lines causes cold spots. Circulation problems leave parts of the house cold while the boiler runs constantly. Pressure issues can indicate leaks or expansion tank problems.

Understanding how the whole system works together is essential for proper troubleshooting. Fixing individual components without understanding the system often doesn't solve the real problem.`,
        services: [
          'Troubleshoot boiler and hydronic system issues',
          'Repair system components within scope',
          'Replace boilers when repair no longer makes sense',
          'Service circulators, zone valves, and controls',
          'Purge air and balance the system',
          'Review overall system performance and operation'
        ],
        whyChoose: `Hydronic heating requires system-level thinking—not just swapping parts. We understand how boilers, circulation, zones, and controls work together, and we troubleshoot accordingly.

DNB serves Calgary homeowners with hot water heating systems. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Boiler & Hydronic Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'In-Floor Radiant Heating',
        slug: 'in-floor-radiant-heating-calgary',
        metaTitle: 'In-Floor Radiant Heating Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Radiant floor heating installation in Calgary. Heated floors for basements, bathrooms, slabs. Call 403-615-8770.',
        heroImage: 'radiant',
        serviceType: 'install',
        intro: `Radiant floor heating uses tubing embedded in or beneath the floor to deliver even, comfortable warmth that rises from below. It's quiet, efficient, and eliminates the drafts and hot spots you get with forced air. Popular for basements, bathrooms, slabs, and whole-home heating, it's a premium comfort upgrade.

DNB designs and installs hydronic radiant heating systems throughout Calgary. If you're building, renovating, or adding heated floors to specific areas, we can help you plan and install a system that works.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re finishing a basement and want comfortable floors',
          'You want heated bathroom floors',
          'You\'re building or renovating with slab-on-grade construction',
          'You want more even, consistent heat than forced air provides',
          'Existing radiant zones are underperforming',
          'You want a premium comfort upgrade in key areas of the home'
        ],
        whyMatters: `Radiant floor heating is one of the most comfortable heating options available—but it has to be designed and installed properly. The tubing layout affects how evenly the floor heats. The loop lengths affect system balance. The controls determine how zones respond to demand.

Mistakes at installation stage are expensive to fix. Getting the design right before the floor goes down means even, reliable heat for years to come.`,
        services: [
          'Plan radiant layout based on room size and heat loss',
          'Install tubing and related components',
          'Set up manifolds and zoning as needed',
          'Modify or repair existing radiant systems',
          'Support startup and system commissioning',
          'Integrate radiant with existing heating where applicable'
        ],
        whyChoose: `We approach radiant heating as a designed system, not just tubing thrown into a floor. Proper layout, proper control, and proper installation mean the difference between a floor that heats evenly and one that doesn't.

DNB serves Calgary homeowners looking for quality radiant heat installations. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Request In-Floor Heating Quote',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Garage Heaters',
        slug: 'garage-heaters-calgary',
        metaTitle: 'Garage Heaters Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Garage heater installation in Calgary. Natural gas unit heaters for garages and workshops. Call 403-615-8770.',
        heroImage: 'garage',
        serviceType: 'install',
        intro: `A heated garage makes Calgary winters more manageable. Whether you use your garage as a workshop, want to keep your vehicles out of the extreme cold, or just want a usable space year-round, a properly installed gas heater can transform it from a freezing storage area to a functional, comfortable space.

DNB installs garage heaters throughout Calgary. We size the heater for your space, handle the gas connection and venting, and make sure everything is set up safely and correctly.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'Your garage is too cold to use in winter',
          'You spend time working in the garage and want comfortable conditions',
          'You\'re building or upgrading a detached garage or workshop',
          'You want to reduce cold transfer into adjacent living space',
          'Your existing garage heater needs replacement',
          'You want the space usable year-round'
        ],
        whyMatters: `A garage heater isn't just about comfort—though comfort matters when you're spending time out there. It protects vehicles and equipment from extreme cold, reduces moisture and condensation, and can even help the attached living space stay warmer by reducing cold transfer through shared walls.

Proper sizing matters. An undersized heater won't keep up. An oversized one cycles too frequently and wastes energy. Safe installation matters even more—gas heaters need proper venting and combustion air to operate safely.`,
        services: [
          'Assess garage size and heating requirements',
          'Recommend an appropriately sized unit heater',
          'Install and mount the heater properly',
          'Run gas line and make safe connections',
          'Install proper venting and ensure adequate combustion air',
          'Test operation and verify safe startup'
        ],
        whyChoose: `We install garage heaters that are sized right for your space and installed with safety as the priority. Gas, venting, controls—we handle it all and make sure it works properly.

DNB is a Calgary company focused on practical heating solutions. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Garage Heater Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Thermostats & Controls',
        slug: 'thermostats-controls-calgary',
        metaTitle: 'Thermostat Installation Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Thermostat upgrades and installation in Calgary. Smart thermostats, programmable controls. Call 403-615-8770.',
        heroImage: 'thermostat',
        serviceType: 'service',
        intro: `Thermostats and heating controls are the command center for your home's comfort. They determine when the system runs, how it responds to temperature changes, and how efficiently your heating operates. An old, inaccurate, or poorly installed thermostat can make even a good furnace perform badly.

DNB installs and replaces thermostats and heating controls throughout Calgary. Whether you want a simple upgrade or a full smart thermostat setup, we make sure it's installed correctly and works with your system.`,
        section2Title: 'Why Homeowners Book This Service',
        section2Items: [
          'Home temperature never seems to match the thermostat setting',
          'Current thermostat is outdated, inaccurate, or unreliable',
          'Heating system isn\'t responding properly to temperature calls',
          'You want programmable scheduling to match your routine',
          'You want smart thermostat features and remote control',
          'You\'re upgrading equipment and need compatible controls'
        ],
        whyMatters: `The thermostat controls everything. If it's reading temperature wrong, the system responds wrong. If it's not communicating properly with the furnace, the system may short cycle, run constantly, or fail to start. If it's an old manual dial, you're missing out on scheduling and efficiency features.

A good thermostat improves comfort, reduces energy waste, and gives you better control over your home's heating. It's one of the simplest upgrades with one of the biggest impacts.`,
        services: [
          'Replace faulty or outdated thermostats',
          'Install programmable thermostats',
          'Install smart/WiFi thermostats',
          'Verify wiring compatibility with your system',
          'Configure settings and schedules',
          'Confirm proper communication with heating equipment'
        ],
        whyChoose: `We install thermostats that actually work with your system—not just what looks good on the wall. Compatibility, wiring, and proper configuration all matter. We make sure everything communicates properly before we leave.

DNB serves Calgary homeowners. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Thermostat Upgrade Service',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      }
    ]
  },
  'gas-fitting': {
    name: 'Gas Fitting',
    slug: 'gas-fitting',
    description: 'Licensed gas fitting services in Calgary',
    icon: 'gauge',
    subServices: [
      {
        name: 'Gas Line Installation',
        slug: 'gas-line-installation-calgary',
        metaTitle: 'Gas Line Installation Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Gas line installation in Calgary. Stoves, BBQs, fireplaces, garage heaters. Licensed gas fitters. Call 403-615-8770.',
        heroImage: 'gas-line',
        serviceType: 'install',
        intro: `Gas line installation provides fuel to appliances throughout your home—stoves and ranges, BBQs, fireplaces, garage heaters, dryers, and more. Whether you're adding a new appliance, renovating, or running gas to a new area of the house, proper installation is essential for safe, reliable operation.

DNB provides licensed gas fitting services throughout Calgary. We size lines correctly, route them safely, pressure test the system, and make sure everything is up to code.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re adding a new gas appliance',
          'You need a gas connection for a renovation project',
          'You\'re building an outdoor cooking or heating setup',
          'You need gas run to a garage heater or fireplace',
          'You want to convert from electric to gas where appropriate',
          'An existing line needs to be extended or relocated'
        ],
        whyMatters: `Gas line work isn't something to take chances with. Lines need to be properly sized for the appliances they serve. Routing needs to follow code requirements. The entire system needs to be pressure tested to confirm there are no leaks. Cutting corners on gas work creates safety hazards that can have serious consequences.

Proper installation means safe, reliable gas supply to your appliances for years to come.`,
        services: [
          'Review appliance requirements and plan the route',
          'Size gas piping correctly for demand',
          'Install black iron or CSST gas lines to code',
          'Add shutoffs and connection points as required',
          'Pressure test the entire system',
          'Confirm safe operation and proper connections'
        ],
        whyChoose: `Gas work requires licensed professionals who understand the codes and take safety seriously. We don't cut corners—we do it right, test it thoroughly, and make sure everything is safe before we leave.

DNB is a licensed Calgary gas fitting company. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Call for Gas Line Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Gas Safety & Testing',
        slug: 'gas-safety-testing-calgary',
        metaTitle: 'Gas Safety Testing Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Gas leak detection and safety testing in Calgary. Professional gas system inspection. Call 403-615-8770.',
        heroImage: 'gas-line',
        serviceType: 'compliance',
        intro: `If you smell gas, suspect a leak, or something about your gas system doesn't seem right, it needs to be checked. Gas safety testing identifies leaks, unsafe conditions, and problems with gas appliances or connections before they become dangerous.

DNB provides gas safety and leak testing services throughout Calgary. We take gas concerns seriously and respond accordingly.`,
        section2Title: 'When This Service Is Required',
        section2Items: [
          'You smell gas in or around your home',
          'You suspect a gas leak',
          'A gas appliance isn\'t operating properly',
          'You want a safety check after recent gas work',
          'Your carbon monoxide detector has alarmed',
          'You want verification before using equipment'
        ],
        whyMatters: `Gas leaks are serious. Natural gas is combustible, and even small leaks can accumulate to dangerous levels. Improper combustion produces carbon monoxide—an odourless gas that can be fatal. If something doesn't seem right with your gas system, it needs professional attention.

This isn't the time for guessing or hoping it's nothing. Getting it checked properly protects your home and your family.`,
        services: [
          'Perform leak testing with professional detection equipment',
          'Check visible gas connections and fittings',
          'Inspect for unsafe conditions',
          'Test carbon monoxide levels if concerns exist',
          'Identify issues that need correction',
          'Explain findings and recommend next steps'
        ],
        whyChoose: `We take gas safety seriously. We use proper detection equipment, check connections thoroughly, and provide clear information about what we find. If there's a problem, we tell you exactly what needs to happen next.

DNB is a licensed Calgary gas fitting company focused on safety and professionalism.`,
        cta: 'Book Gas Safety Testing',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'BBQ Gas Lines',
        slug: 'bbq-gas-lines-calgary',
        metaTitle: 'BBQ Gas Lines Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Natural gas BBQ hookup in Calgary. Permanent gas connection for outdoor grills. Call 403-615-8770.',
        heroImage: 'bbq',
        serviceType: 'install',
        intro: `A permanent natural gas line for your BBQ means no more running out of propane mid-cookout, no more hauling tanks to get refilled, and no more dealing with exchanges. You get a cleaner setup, consistent fuel supply, and the convenience of turning on the grill whenever you want.

DNB installs BBQ gas lines throughout Calgary. We run a dedicated line to your grill location, install a proper shutoff and connection point, and make sure everything is safe and up to code.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re tired of refilling or exchanging propane tanks',
          'You use your BBQ frequently and want reliable fuel supply',
          'You\'re building an outdoor kitchen or upgraded patio space',
          'You want a cleaner, more permanent setup',
          'You\'re adding other outdoor gas features like a fire pit or patio heater'
        ],
        whyMatters: `A natural gas BBQ connection is one of those upgrades that makes life easier every time you use it. No more wondering if you have enough propane left. No more mid-burger tank swaps. Just turn it on and cook.

It also looks cleaner—no tank sitting next to the grill—and natural gas is generally less expensive than propane over time. Once it's installed, you'll wonder why you didn't do it sooner.`,
        services: [
          'Run a dedicated gas line to the BBQ location',
          'Install shutoff valve and quick-connect fitting',
          'Use underground or surface routing depending on the setup',
          'Pressure test the system',
          'Verify connection compatibility with your grill',
          'Leave a clean, functional installation'
        ],
        whyChoose: `We install BBQ gas lines that are routed cleanly, connected safely, and tested thoroughly. Whether it's a simple deck connection or part of a larger outdoor setup, we do it right.

DNB is a licensed Calgary gas fitting company. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book BBQ Gas Line Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Gas Appliance Installation',
        slug: 'gas-appliance-installation-calgary',
        metaTitle: 'Gas Appliance Installation Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Gas appliance hookups in Calgary. Stoves, dryers, ranges, heaters. Safe connections. Call 403-615-8770.',
        heroImage: 'gas-appliance',
        serviceType: 'install',
        intro: `When you purchase a new gas range, dryer, or other gas appliance, it needs to be connected properly before you can use it. This isn't just plugging something in—gas connections require proper fittings, leak testing, and verification that everything is safe and operating correctly.

DNB provides gas appliance installation services throughout Calgary. We connect your appliances safely, test for leaks, and make sure everything is ready to use.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You purchased a new gas range, stove, or cooktop',
          'You\'re installing a gas dryer',
          'You\'re relocating a gas appliance to a different spot',
          'You\'re replacing an old appliance with a new one',
          'You want a professional hookup instead of doing it yourself',
          'You\'re converting from electric to gas'
        ],
        whyMatters: `A gas appliance that isn't connected properly can leak. A leak you don't notice can accumulate. It's a simple chain of events that leads to a serious safety hazard. Professional installation ensures the connection is correct, the fittings are tight, and there are no leaks.

It also means the appliance will operate correctly from the start—proper gas supply, proper flame, proper performance.`,
        services: [
          'Connect gas appliances safely and correctly',
          'Verify gas supply and connection compatibility',
          'Use proper fittings and connectors',
          'Test all connections for leaks',
          'Confirm appliance operation',
          'Safely disconnect and cap old equipment when needed'
        ],
        whyChoose: `We connect gas appliances with attention to detail and proper testing. Every connection gets checked for leaks before we leave. Your safety isn't something we take shortcuts on.

DNB is a licensed Calgary gas fitting company. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Book Gas Appliance Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      },
      {
        name: 'Gas Fireplace Installation',
        slug: 'gas-fireplace-calgary',
        metaTitle: 'Gas Fireplace Installation Calgary | DNB Plumbing, Heating & Gas Fitting Inc.',
        metaDescription: 'Gas fireplace hookup and installation in Calgary. Safe gas connections for fireplaces. Call 403-615-8770.',
        heroImage: 'fireplace',
        serviceType: 'install',
        intro: `A gas fireplace adds warmth, ambiance, and style to any room. It's cleaner than wood burning, easier to control, and can provide supplemental heat during cold Calgary winters. But like any gas appliance, it needs to be connected properly and safely.

DNB handles the gas connection side of fireplace installations throughout Calgary. We run the gas line, make the connection, and ensure everything is safe and functioning before you use it.`,
        section2Title: 'When This Service Makes Sense',
        section2Items: [
          'You\'re adding a new gas fireplace during a renovation',
          'You\'re finishing a basement and want a fireplace feature',
          'You\'re replacing an older fireplace insert',
          'You want supplemental heat and the ambiance of a real flame',
          'You\'re converting a wood fireplace to gas',
          'You need a proper gas connection for your fireplace project'
        ],
        whyMatters: `Gas fireplaces combine the look of a real fire with the convenience of gas. But the gas connection needs to be done right—proper line sizing, safe routing, secure connections, and thorough leak testing. A fireplace that isn't connected properly is a safety hazard sitting in your living room.

Getting the gas work done correctly means safe, reliable operation every time you turn it on.`,
        services: [
          'Run gas line to the fireplace location',
          'Coordinate with fireplace installer if needed',
          'Make secure, properly sized connections',
          'Pressure test and check for leaks',
          'Verify pilot and main burner operation',
          'Ensure safe, functional installation'
        ],
        whyChoose: `We handle the gas side of fireplace installations with the same attention to safety as any other gas work. Clean connections, thorough testing, and safe operation—that's what we deliver.

DNB is a licensed Calgary gas fitting company. Supplied materials are backed by a 1-year parts and labour warranty.`,
        cta: 'Request Gas Fireplace Installation',
        ctaHelper: 'Need help deciding what you need? Call 403-615-8770 and we\'ll point you in the right direction.'
      }
    ]
  }
};

// Helper function to get all sub-services for navigation
export const getNavigation = () => {
  return Object.values(serviceStructure).map(service => ({
    name: service.name,
    slug: service.slug,
    subServices: service.subServices.map(sub => ({
      name: sub.name,
      slug: sub.slug,
      path: `/${service.slug}/${sub.slug}`
    }))
  }));
};

// Helper function to find a sub-service by slugs
export const findSubService = (mainSlug, subSlug) => {
  const mainService = serviceStructure[mainSlug];
  if (!mainService) return null;
  
  return mainService.subServices.find(sub => sub.slug === subSlug);
};

// Helper function to get main service
export const getMainService = (slug) => {
  return serviceStructure[slug];
};

export default serviceStructure;
